import { Loader2, X } from "@digital-twins/design-system/icons";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@design-system/components/dialog";
import { Input } from "@design-system/components/input";
import type { EditProfileDialogProps } from "@/types/components/settings/edit-profile-dialog";

const ORG_TYPE_LABEL_KEYS: Record<string, 'personal' | 'agency' | 'general'> = {
  personal: 'personal',
  agency: 'agency',
  general: 'general',
};

export function EditProfileDialog({
  open,
  onOpenChange,
  editName,
  onEditNameChange,
  profileEmail,
  saving,
  onSave,
  orgType,
  hasOrganization,
}: EditProfileDialogProps) {
  const { t } = useTranslation(['app', 'common']);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-background dark:bg-card border-border dark:border-[hsl(var(--card-inset))] max-w-[480px] rounded-2xl shadow-[0px_4px_19.6px_0px_rgba(0,0,0,0.08)] p-0 sm:p-0 gap-0 [&>button]:hidden">
        <div className="flex items-center justify-end p-3">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-muted-foreground hover:text-foreground dark:text-white/60 dark:hover:text-white transition-colors"
            aria-label={t('close', { ns: 'common' })}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col gap-6 px-10 pb-10">
          <div className="space-y-1">
            <DialogTitle className="font-display text-[24px] font-normal text-foreground dark:text-white tracking-[-0.48px] leading-[1.2]">
              {t('settingsProfile.update')}
            </DialogTitle>
            <p className="text-[14px] text-muted-foreground leading-[1.6]">
              How should we address you?
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="name" className="text-[13px] font-medium text-muted-foreground">
                Your name
              </label>
              <Input
                id="name"
                value={editName}
                onChange={(event) => onEditNameChange(event.target.value)}
                placeholder={t('settingsProfile.namePlaceholder')}
                className="rounded-[6px] border-input bg-background dark:bg-[hsl(var(--card-inset))] text-foreground dark:text-white placeholder:text-muted-foreground/50 dark:placeholder:text-white/30 focus:border-input-focus/50 focus:ring-input-focus/20"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-[13px] font-medium text-muted-foreground">
                Email
              </label>
              <Input
                id="email"
                value={profileEmail}
                disabled
                className="rounded-[6px] border-input bg-muted dark:bg-[hsl(var(--card-inset))]/60 text-muted-foreground dark:text-white/50"
              />
              <p className="text-[12px] text-muted-foreground/60">
                This can&apos;t be changed
              </p>
            </div>

            {hasOrganization && (
              <div className="space-y-1.5">
                <label className="text-[13px] font-medium text-muted-foreground">
                  Organization Type
                </label>
                <Input
                  value={orgType && ORG_TYPE_LABEL_KEYS[orgType]
                    ? t(`settingsProfile.${ORG_TYPE_LABEL_KEYS[orgType]}`)
                    : ''}
                  disabled
                  readOnly
                  data-testid="org-type-readonly"
                  className="rounded-[6px] border-input bg-muted dark:bg-[hsl(var(--card-inset))]/60 text-muted-foreground dark:text-white/50"
                />
                <p className="text-[12px] text-muted-foreground/60">
                  Plan-driven — change your plan to update org type.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-center gap-3 pt-2">
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="h-10 rounded-[6px] px-6 text-[16px] font-medium bg-cta text-cta-foreground hover:bg-cta/90 disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('saving', { ns: 'common' })}
                </span>
              ) : (
                t('save', { ns: 'common' })
              )}
            </button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="h-10 rounded-[6px] px-6 text-[16px] font-medium border border-foreground dark:border-input-focus bg-transparent text-foreground dark:text-input-focus hover:bg-foreground/10 dark:hover:bg-input-focus/10 transition-colors"
            >
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
